import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Save, Loader2, Gift, RefreshCw, Search, ExternalLink } from "lucide-react";

const fmt = (n) => `$${Math.round(n || 0).toLocaleString("es-CO")}`;

export default function AdminClub() {
  const [config, setConfig] = useState(null);
  const [productos, setProductos] = useState([]);
  const [beneficios, setBeneficios] = useState(null);
  const [q, setQ] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  const cargarBeneficios = (query = "") => {
    api.get(`/club/admin/beneficios${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((r) => setBeneficios(r.data));
  };

  useEffect(() => {
    Promise.all([api.get("/club/admin/config"), api.get("/productos")]).then(([c, p]) => {
      setConfig(c.data);
      setProductos(p.data.filter((x) => x.activo));
    });
    cargarBeneficios();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put("/club/admin/config", config);
      setConfig(r.data);
      toast.success("Club actualizado");
    } catch {
      toast.error("Error guardando");
    } finally {
      setGuardando(false);
    }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await api.post("/club/admin/sincronizar");
      toast.success(`${r.data.clientes_procesados} clientes procesados, ${r.data.beneficios_otorgados} beneficios nuevos otorgados`);
      cargarBeneficios(q);
    } catch {
      toast.error("Error sincronizando");
    } finally {
      setSincronizando(false);
    }
  };

  const redimir = async (id) => {
    try {
      await api.post(`/club/admin/beneficios/${id}/redimir`, {});
      toast.success("Beneficio marcado como redimido");
      cargarBeneficios(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error redimiendo");
    }
  };

  const setNivel = (idx, campo, valor) => {
    const niveles = [...config.niveles];
    niveles[idx] = { ...niveles[idx], [campo]: valor };
    setConfig({ ...config, niveles });
  };

  const setRecompensa = (idx, campo, valor) => {
    const niveles = [...config.niveles];
    niveles[idx] = { ...niveles[idx], recompensa: { ...niveles[idx].recompensa, [campo]: valor } };
    setConfig({ ...config, niveles });
  };

  if (!config) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-torta-brown" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-club-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-torta-dark flex items-center gap-2"><Crown className="w-7 h-7 text-amber-500" /> Club de Fidelidad</h1>
          <p className="text-torta-muted text-sm">Niveles, recompensas y beneficios del Club de la Abuela</p>
        </div>
        <div className="flex gap-2">
          <a href="/club" target="_blank" rel="noreferrer"><Button variant="outline"><ExternalLink className="w-4 h-4 mr-1" /> Ver club</Button></a>
          <Button data-testid="guardar-club-btn" onClick={guardar} disabled={guardando} className="bg-torta-brown hover:bg-torta-brown/90">
            {guardando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg text-torta-dark">Configuración general</h2>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Club activo</Label>
            <Switch data-testid="club-activo-switch" checked={config.activo} onCheckedChange={(v) => setConfig({ ...config, activo: v })} />
          </div>
        </div>
        <div><Label className="text-xs">Nombre del club</Label><Input value={config.nombre} onChange={(e) => setConfig({ ...config, nombre: e.target.value })} /></div>
        <div><Label className="text-xs">Descripción (visible al cliente)</Label><Textarea rows={2} value={config.descripcion} onChange={(e) => setConfig({ ...config, descripcion: e.target.value })} /></div>
        <div><Label className="text-xs">URL de la foto del club (hero de la landing)</Label>
          <Input data-testid="club-imagen-input" placeholder="https://..." value={config.imagen_url || ""} onChange={(e) => setConfig({ ...config, imagen_url: e.target.value })} /></div>
        {config.imagen_url && <img src={config.imagen_url} alt="foto club" className="h-32 rounded-xl object-cover" />}
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
        <h2 className="font-display text-lg text-torta-dark">Niveles (modo mixto: compras O facturación, lo que llegue primero)</h2>
        {config.niveles.map((n, idx) => (
          <div key={n.id} className="border border-torta-sand rounded-xl p-4 space-y-3" data-testid={`nivel-admin-${n.id}`}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label className="text-xs">Emoji</Label><Input value={n.emoji} onChange={(e) => setNivel(idx, "emoji", e.target.value)} /></div>
              <div><Label className="text-xs">Nombre</Label><Input value={n.nombre} onChange={(e) => setNivel(idx, "nombre", e.target.value)} /></div>
              <div><Label className="text-xs">Mín. compras</Label><Input type="number" data-testid={`min-compras-${n.id}`} value={n.min_compras} onChange={(e) => setNivel(idx, "min_compras", parseInt(e.target.value) || 0)} /></div>
              <div><Label className="text-xs">Mín. facturación ($)</Label><Input type="number" value={n.min_facturacion} onChange={(e) => setNivel(idx, "min_facturacion", parseInt(e.target.value) || 0)} /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 bg-torta-cream/40 rounded-lg p-3">
              <div>
                <Label className="text-xs">Tipo de recompensa</Label>
                <Select value={n.recompensa.tipo} onValueChange={(v) => setRecompensa(idx, "tipo", v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="producto">🎁 Producto de regalo</SelectItem>
                    <SelectItem value="descuento">% Descuento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {n.recompensa.tipo === "producto" ? (
                <div>
                  <Label className="text-xs">Producto regalo</Label>
                  <Select value={n.recompensa.producto_id || ""} onValueChange={(v) => setRecompensa(idx, "producto_id", v)}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      {productos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div><Label className="text-xs">Descuento %</Label><Input type="number" value={n.recompensa.descuento_pct || ""} onChange={(e) => setRecompensa(idx, "descuento_pct", parseInt(e.target.value) || 0)} /></div>
              )}
              <div><Label className="text-xs">Descripción visible</Label><Input value={n.recompensa.descripcion} onChange={(e) => setRecompensa(idx, "descripcion", e.target.value)} /></div>
              <div><Label className="text-xs">Vigencia (días, vacío = sin vencimiento)</Label>
                <Input type="number" data-testid={`vigencia-${n.id}`} value={n.recompensa.vigencia_dias || ""} placeholder="Sin vencimiento" onChange={(e) => setRecompensa(idx, "vigencia_dias", e.target.value ? parseInt(e.target.value) : null)} /></div>
              <div><Label className="text-xs">Compra mínima de redención ($, 0 = sin mínimo)</Label>
                <Input type="number" data-testid={`compra-minima-${n.id}`} value={n.recompensa.compra_minima || ""} placeholder="0" onChange={(e) => setRecompensa(idx, "compra_minima", e.target.value ? parseInt(e.target.value) : 0)} /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display text-lg text-torta-dark flex items-center gap-2"><Gift className="w-5 h-5" /> Beneficios otorgados</h2>
          <Button variant="outline" size="sm" data-testid="sincronizar-club-btn" onClick={sincronizar} disabled={sincronizando}>
            {sincronizando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sincronizar clientes
          </Button>
        </div>
        {beneficios && (
          <>
            <div className="flex gap-2 text-center">
              {[["total", "Total"], ["disponibles", "Disponibles"], ["redimidos", "Redimidos"]].map(([k, l]) => (
                <div key={k} className="flex-1 bg-torta-cream/50 rounded-xl p-2">
                  <p className="text-lg font-bold text-torta-dark">{beneficios.resumen[k]}</p>
                  <p className="text-[10px] text-torta-muted">{l}</p>
                </div>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); cargarBeneficios(q); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-torta-muted" />
                <Input className="pl-9" placeholder="Buscar por nombre, celular o código..." value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <Button type="submit" variant="outline">Buscar</Button>
            </form>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {beneficios.beneficios.map((b) => (
                <div key={b.id} className="border border-torta-sand rounded-xl px-3 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`admin-beneficio-${b.codigo}`}>
                  <div className="flex-1 min-w-[160px]">
                    <p className="text-sm font-semibold text-torta-dark">{b.cliente_nombre || b.customer_id} <span className="text-xs font-normal text-torta-muted">· {b.phone_normalized}</span></p>
                    <p className="text-xs text-torta-muted">{b.nivel_emoji} {b.nivel_nombre} · {b.recompensa?.descripcion} · <strong>{b.codigo}</strong></p>
                  </div>
                  <Badge className={`border-0 ${b.estado === "disponible" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>{b.estado}</Badge>
                  {b.estado === "disponible" && (
                    <Button size="sm" variant="outline" data-testid={`redimir-${b.codigo}`} onClick={() => redimir(b.id)}>Marcar redimido</Button>
                  )}
                  {b.estado === "redimido" && <p className="text-[10px] text-torta-muted">por {b.redimido_por} {b.orden_id ? "· en tienda" : ""}</p>}
                </div>
              ))}
              {beneficios.beneficios.length === 0 && <p className="text-center text-sm text-torta-muted py-6">Sin beneficios aún. Usa "Sincronizar clientes" para otorgar según compras históricas.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
